import { useI18N } from '@yandex-int/i18n/lib/react';
import React, { useEffect, useState } from 'react';

import { Loader, RadioGroup, RadioGroupOption } from '@gravity-ui/uikit';
import { ApiError, getCloudFunctions, validateCloudFunction } from 'client/api';
import { CloudError, CloudFunction } from 'client/types/cloud';
import * as keyset from 'i18n/SmartHomeForm';

import styles from './BackendTypeField.module.scss';
import {
    EmptyFunctionsDescription,
    FunctionsDisclaimer,
    getFunctionListErrorMessages,
    getValidateFunctionErrorMessages,
} from './texts';

interface FunctionsContainerProps {
    onFunctionIdChange: (value: string) => void;
    value: string;
    disabled?: boolean;
}

const FunctionsContainer: React.FC<FunctionsContainerProps> = ({ onFunctionIdChange, disabled, value }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [functions, setFunctions] = useState<CloudFunction[]>([]);
    const [functionsListErrorCode, setFunctionsListErrorCode] = useState<CloudError | undefined>(undefined);
    const [validationErrorCode, setValidationErrorCode] = useState<CloudError | undefined>(undefined);

    const errorMessages = getValidateFunctionErrorMessages();
    const errorFnListMessages = getFunctionListErrorMessages();

    const t = useI18N(keyset);

    const loadFunctions = async () => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);

        try {
            setFunctions(await getCloudFunctions());
        } catch (e) {
            if (e instanceof ApiError && e.payload && e.payload.errorCode) {
                setFunctionsListErrorCode(e.payload.errorCode as CloudError);
            }
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const validateFunctionOnChange = async () => {
        setValidationErrorCode(undefined);

        if (!value) {
            return;
        }

        try {
            await validateCloudFunction(value);
        } catch (e) {
            if (e instanceof ApiError && e.payload && e.payload.errorCode) {
                setValidationErrorCode(e.payload.errorCode as CloudError);

                return;
            }

            setValidationErrorCode(CloudError.UNKNOWN);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void loadFunctions(), []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void validateFunctionOnChange(), [value]);

    if (isLoading) {
        return <Loader className={styles.formElement} size="m" />;
    }

    if (functionsListErrorCode) {
        return <>{errorFnListMessages?.[functionsListErrorCode]}</>;
    }

    if (functions.length === 0) {
        return <EmptyFunctionsDescription />;
    }

    const options: RadioGroupOption[] = [{ value: '', content: t('not-chosen') }];
    functions.forEach((fn) => {
        options.push({ value: fn.functionId, content: `${fn.cloudName}/${fn.folderName}/${fn.functionName}` });
    });

    return (
        <>
            <RadioGroup
                className={styles.functionsRadioGroup}
                name="cloud_functions"
                options={options}
                onUpdate={onFunctionIdChange}
                disabled={disabled}
                value={value}
                defaultValue={options[0].value}
                direction="vertical"
                size="l"
            />
            {validationErrorCode ? (
                <div className={styles.functionValidateErrorLabel}>{errorMessages?.[validationErrorCode]}</div>
            ) : null}
            <FunctionsDisclaimer />
        </>
    );
};

export default FunctionsContainer;
